import './TicketShowServiceOrders.scss';

import { DateField } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Card, Empty, Skeleton, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { usePermissionContext } from '@/contexts/PermissionContext';
import {
  ServiceOrder,
  ServiceOrderStatusString,
  ServiceOrderTypeString,
} from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';
import { RESOURCES } from '@/Resources';

interface TicketShowServiceOrdersProps {
  record?: Ticket;
}

const TicketShowServiceOrders = (props: TicketShowServiceOrdersProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { record } = props;
  const {
    result: { data: serviceOrders },
    query: { isLoading },
  } = useList<ServiceOrder>({
    resource: 'service_orders',
    meta: {
      tickets: [record?.id],
    },
    queryOptions: {
      enabled: !!record?.id,
    },
  });
  const getTechnicianLabel = (serviceOrder: ServiceOrder) => {
    const technicianName = (serviceOrder.technician as any)?.nombre ?? serviceOrder.technician;

    return technicianName ? `${technicianName}` : '-';
  };

  const getCompanyLabel = (serviceOrder: ServiceOrder) => {
    const companyName = (serviceOrder.company as any)?.full_name ?? serviceOrder.company;

    return companyName ? `${companyName}` : '-';
  };

  if (isLoading) {
    return (
      <div className="TicketShowServiceOrders">
        <div className="TicketShowServiceOrdersCardsContainer">
          {[1, 2].map(i => (
            <Card key={i}>
              <Skeleton active paragraph={{ rows: 3 }} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="TicketShowServiceOrders">
      <div className="TicketShowServiceOrdersCardsContainer">
        {serviceOrders.length > 0 ? (
          serviceOrders.map(so => (
            <Card className="TicketShowServiceOrdersCard" key={so.id}>
              <div className="TicketShowServiceOrdersCardHeader">
                <div className="TicketShowServiceOrdersCardTitle">
                  {hasPermission('show', RESOURCES.SERVICE_ORDERS) ? (
                    <Link
                      className="TicketShowServiceOrdersCardLink"
                      to={`/${RESOURCES.SERVICE_ORDERS}/show/${so.id}`}>
                      Orden #{so.id}
                    </Link>
                  ) : (
                    <span>Orden #{so.id}</span>
                  )}
                  {so.status != undefined && (
                    <Tag color="blue">{ServiceOrderStatusString[so.status]}</Tag>
                  )}
                </div>
                <div className="TicketShowServiceOrdersCardMeta">
                  <div>
                    <b>{t('Tipo')}:</b> {ServiceOrderTypeString[so.type] ?? '-'}
                  </div>
                  <div>
                    <b>{t('Fecha')}:</b> <DateField value={so.service_date} />
                  </div>
                </div>
              </div>
              <div className="TicketShowServiceOrdersCardDetails">
                <div>
                  <b>{t('Técnico')}:</b> {getTechnicianLabel(so)}
                </div>
                <div>
                  <b>{t('Empresa')}:</b> {getCompanyLabel(so)}
                </div>
              </div>
              {so.description ? (
                <div className="TicketShowServiceOrdersCardDescription">
                  <b>{t('Descripción')}:</b> {so.description}
                </div>
              ) : null}
            </Card>
          ))
        ) : (
          <Empty description={t('No hay órdenes de servicio asociadas')} />
        )}
      </div>
      {hasPermission('create', RESOURCES.SERVICE_ORDERS) && (
        <>
          {record?.empresa && record?.movil ? (
            <Link
              className="TicketShowServiceOrdersLink"
              to={`/service_orders/create?company=${record?.empresa}&ticket=${record?.id}`}>
              {t('Crear nueva orden de servicio')}
            </Link>
          ) : (
            <>
              <Typography.Text
                type="secondary"
                style={{ marginTop: 8, display: 'block', fontSize: 16 }}>
                {t(
                  'Para crear una orden de servicio, el ticket debe tener empresa y móvil asociados',
                )}
              </Typography.Text>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TicketShowServiceOrders;
